<template>
  <header class="page-header" :class="alignmentClass">
    <h1 class="page-title" :class="titleSizeClass">
      <slot />
    </h1>

    <!-- サブタイトルやブレッドクラムなどのオプション要素 -->
    <div v-if="$slots.subtitle" class="page-subtitle">
      <slot name="subtitle" />
    </div>

    <!-- アクションボタンなどのオプション要素 -->
    <div v-if="$slots.actions" class="page-actions">
      <slot name="actions" />
    </div>
  </header>
</template>

<script setup>
import { computed } from 'vue'

/**
 * PageHeader - ページヘッダーコンポーネント (Molecule)
 *
 * ページのタイトル領域を表示するMoleculesコンポーネントです。
 * Atomsを組み合わせて、意味のあるヘッダー構造を提供します。
 *
 * 責務:
 * - ページタイトルの統一された表示
 * - サブタイトル、アクションボタンなどの配置管理
 * - 適切なセマンティックHTML構造の提供
 * - 一貫したヘッダースタイリング
 */

const props = defineProps({
  /**
   * タイトルのサイズ
   * 'sm': 小
   * 'md': 標準
   * 'lg': 大
   * 'xl': 特大
   */
  titleSize: {
    type: String,
    default: 'lg',
    validator: (value) => {
      return ['sm', 'md', 'lg', 'xl'].includes(value)
    }
  },
  /**
   * ヘッダーの配置
   * 'left': 左寄せ
   * 'center': 中央寄せ
   * 'between': 両端寄せ（アクション要素がある場合）
   */
  alignment: {
    type: String,
    default: 'left',
    validator: (value) => {
      return ['left', 'center', 'between'].includes(value)
    }
  }
})

const titleSizeClass = computed(() => ({
  'text-sm': props.titleSize === 'sm',
  'text-md': props.titleSize === 'md',
  'text-lg': props.titleSize === 'lg',
  'text-xl': props.titleSize === 'xl'
}))

const alignmentClass = computed(() => ({
  'text-left': props.alignment === 'left',
  'text-center': props.alignment === 'center',
  'flex justify-between items-center': props.alignment === 'between'
}))
</script>

<style scoped>
.page-header {
  margin-bottom: 1.5rem;
}

.page-title {
  margin: 0;
  font-weight: 700;
  color: #111827;
  line-height: 1.25;
}

.page-subtitle {
  margin-top: 0.5rem;
  color: #6b7280;
  font-size: 1rem;
}

.page-actions {
  margin-top: 1rem;
}

/* タイトルサイズクラス */
.text-lg {
  font-size: 1.125rem;
  line-height: 1.75rem;
}

.text-xl {
  font-size: 1.25rem;
  line-height: 1.75rem;
}

.text-2xl {
  font-size: 1.5rem;
  line-height: 2rem;
}

.text-3xl {
  font-size: 1.875rem;
  line-height: 2.25rem;
}

/* 配置クラス */
.text-left {
  text-align: left;
}

.text-center {
  text-align: center;
}

.flex {
  display: flex;
}

.justify-between {
  justify-content: space-between;
}

.items-center {
  align-items: center;
}

/* レスポンシブ対応 */
@media (max-width: 768px) {
  .page-header {
    margin-bottom: 1rem;
  }

  .page-actions {
    margin-top: 0.75rem;
  }

  .flex {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
